import { sentryPrivacyOptions } from "./lib/sentryPrivacy";
import { createRoot, hydrateRoot } from "react-dom/client";
import { hydrate } from "@tanstack/react-query";
import { probeGateIdentity } from "@/lib/gate-bootstrap";
import { installBrowserSessionCapture } from "@/lib/session-capture";
import { clearStartupState, renderStartupState } from "@/lib/startup-state";
import "./index.css";

declare global {
  interface Window {
    __AERO_DEHYDRATED__?: unknown;
    __AERO_SESSION_CAPTURE__?: ReturnType<typeof installBrowserSessionCapture>;
  }
}

const container = document.getElementById("root")!;
// Server-rendered pages (including the anonymous gate) already have useful
// markup to show while their client chunk loads. Dev and non-prerendered routes
// start with an empty root, so give them a synchronous status instead of the
// blank screenshot that a cold dynamic import otherwise produces.
let canHydrateServerMarkup = Boolean(container.firstElementChild);
if (!canHydrateServerMarkup) {
  renderStartupState(container, "loading");
}

// Five-minute capture is explicitly opt-in, remains in memory, and never sends
// data.  It is intended for a local/manual browser rehearsal before a capacity
// retest, not for production analytics.
if (
  typeof window !== "undefined" &&
  (import.meta.env.DEV ||
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1") &&
  new URLSearchParams(window.location.search).get("__aero_session_capture") === "1"
) {
  window.__AERO_SESSION_CAPTURE__ = installBrowserSessionCapture();
}

async function mountFullApp(replacingGate: boolean): Promise<void> {
  const [{ default: App, queryClient }] = await Promise.all([import("./App")]);
  const dehydratedState = window.__AERO_DEHYDRATED__;
  if (dehydratedState) {
    hydrate(queryClient, dehydratedState);
  }

  const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (sentryDsn) {
    void import("@sentry/react")
      .then((Sentry) => {
        Sentry.init({
          dsn: sentryDsn,
          ...sentryPrivacyOptions,
        });
      })
      .catch(() => {
        /* fail-open: never block app startup on Sentry load failure */
      });
  }

  if (replacingGate || !canHydrateServerMarkup) {
    container.replaceChildren();
    clearStartupState(container);
    createRoot(container).render(<App />);
  } else {
    clearStartupState(container);
    hydrateRoot(container, <App />);
  }
  canHydrateServerMarkup = false;
}

async function mountAnonymousGate(): Promise<void> {
  const { ComingSoonPage } = await import(
    "./components/coming-soon-page"
  );
  const message = container.dataset.aeroGateMessage?.trim() || null;
  const gate = (
    <ComingSoonPage
      message={message}
      hasPortal={false}
      session={{ state: "anonymous" }}
    />
  );
  if (canHydrateServerMarkup) {
    clearStartupState(container);
    hydrateRoot(container, gate);
  } else {
    container.replaceChildren();
    clearStartupState(container);
    createRoot(container).render(gate);
  }
  canHydrateServerMarkup = false;
}

/**
 * A server-rendered anonymous gate is marked on the root element. Before
 * hydrating it, perform one tiny same-origin identity probe:
 *
 * - anonymous session: hydrate only the dependency-light waitlist page;
 * - authenticated session: hand off to the existing full app, which remains
 *   the source of truth for admin/owner bypasses and sign-out state;
 * - network/server error: fail open to the full app rather than risking an
 *   administrator lockout.
 */
async function boot(): Promise<void> {
  if (container.dataset.aeroGate !== "coming-soon") {
    await mountFullApp(false);
    return;
  }

  const decision = await probeGateIdentity();
  if (decision === "anonymous") {
    await mountAnonymousGate();
    return;
  }
  await mountFullApp(true);
}

let bootInFlight = false;

/**
 * Keep import failures attributable instead of leaving a blank root or an
 * unhandled promise rejection. A retry reruns the same identity decision, so
 * an auth failure can never accidentally turn into anonymous access.
 */
async function startBoot(): Promise<void> {
  if (bootInFlight) return;
  bootInFlight = true;
  try {
    await boot();
  } catch (error) {
    canHydrateServerMarkup = false;
    // Preserve the original error in the browser console for diagnosis while
    // showing a stable, user-safe message in the page.
    console.error("[AERO startup] failed to load the application", error);
    const retry = renderStartupState(container, "error");
    retry?.addEventListener("click", () => {
      renderStartupState(container, "loading");
      void startBoot();
    });
  } finally {
    bootInFlight = false;
  }
}

void startBoot();
